/**
 * Shared Mic Stream Hook
 *
 * Acquires microphone permission ONCE and exposes the MediaStream for
 * multiple consumers (session recording, phoneme capture, etc.).
 *
 * Callers must NOT stop the stream's tracks — call release() when the
 * session is fully done.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import {
  isMediaDevicesSupported,
  isSecureContext,
  requestMicrophone,
  stopMediaStream,
  getMediaErrorDetails,
} from '@/lib/media';

export interface UseMicStreamResult {
  streamRef: React.MutableRefObject<MediaStream | null>;
  acquire: () => Promise<MediaStream | null>;
  release: () => void;
  hasPermission: boolean;
  error: string | null;
}

export function useMicStream(): UseMicStreamResult {
  const streamRef = useRef<MediaStream | null>(null);
  const [hasPermission, setHasPermission] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const acquire = useCallback(async (): Promise<MediaStream | null> => {
    if (streamRef.current) return streamRef.current;

    if (!isMediaDevicesSupported() || !isSecureContext()) {
      setError('Microphone requires HTTPS and a supported browser.');
      return null;
    }

    try {
      const stream = await requestMicrophone({
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      });
      streamRef.current = stream;
      setHasPermission(true);
      setError(null);
      return stream;
    } catch (err) {
      const details = getMediaErrorDetails(err);
      setError(details.message);
      setHasPermission(false);
      return null;
    }
  }, []);

  const release = useCallback(() => {
    stopMediaStream(streamRef.current);
    streamRef.current = null;
  }, []);

  useEffect(() => release, [release]);

  return { streamRef, acquire, release, hasPermission, error };
}
